// One game day. Order per DESIGN §9.1 (+ security §12, safety §15, research §13).
import { tickMarket } from '../economy/market.js';
import { tickTimers } from './timers.js';
import { tickDrilling } from './drilling.js';
import { tickProduction } from './production.js';
import { tickRefining } from './refining.js';
import { tickRotary } from './m2tick.js'; // M2 rotary drilling: well control, rival drainage, inspections
import { tickSecurity } from './security.js';
import { tickCrew } from './crew.js';
import { tickSafety } from './safety.js';
import { tickResearch, patentIncome } from './research.js';
import { tickContracts } from './contracts.js';
import { tickBurnAlert } from './austerity.js';
import { tickFinance, rollMonth } from './finance.js';
import { tickLeases } from './survey.js';
import { tickNews, tickEra, tickAchievements } from './events.js';
import { autosave } from '../core/save.js';
import { setInTick } from './indexes.js';
import { tickM2 } from './m2/index.js'; // M2 economy & politics (taxes, prorationing, politics, licensing)
import { tickM3Econ } from './m3/econtick.js'; // M3 econ: nationalizations, lease rounds, hedging, regimes (inert < 1946)
import { tickM3Weather, tickM3Ops } from './m3/opstick.js';
import { tickM4Pre, tickM4Post, tickM4Econ } from './m4/index.js'; // M4 (2000–2029): shale, markets, world (inert < 2000) // M3 ops: storms & sea state, platforms, logistics, charters, EOR, megaprojects

// Patent royalties are scaled down so a patent is a nice income, not a jackpot (tuned with the bot).
export const PATENT_INCOME_MULT = 0.02;

// Perf mode (scripts/perf-sim.mjs): setTickProfiler({}) accumulates ms per subsystem into that object; null turns it
// off. Off, tick() pays one null check — the timed path is a separate function.
let PROF = null;
export function setTickProfiler(obj) { PROF = obj || null; }
const now = () => performance.now();
function timed(name, fn, state, arg) {
  const t = now();
  fn(state, arg);
  const dt = now() - t;
  PROF[name] = (PROF[name] ?? 0) + dt;
  const mx = (PROF['#max'] ??= {});
  if (!(mx[name] >= dt)) mx[name] = dt;
  if (PROF['#tick']) PROF['#tick'][name] = (PROF['#tick'][name] ?? 0) + dt;
}
const patentRoll = (s) => patentIncome(s) * PATENT_INCOME_MULT;
const STEPS = [
  ['market', tickMarket], ['leases', tickLeases], ['timers', tickTimers], ['m3Weather', tickM3Weather], ['m4Pre', tickM4Pre], ['drilling', tickDrilling],
  ['production', tickProduction], ['rotary', tickRotary], ['m3Ops', tickM3Ops], ['m4Post', tickM4Post], ['refining', tickRefining], ['security', tickSecurity],
  ['crew', tickCrew], ['safety', tickSafety], ['research', tickResearch], ['contracts', tickContracts], ['m2', tickM2],
  ['m3Econ', tickM3Econ], ['m4Econ', tickM4Econ], ['finance', tickFinance], ['burnAlert', tickBurnAlert], ['news', tickNews], ['era', tickEra],
  ['achievements', tickAchievements], ['autosave', autosave],
];
function tickProfiled(state) {
  state.day += 1;
  timed('rollMonth', rollMonth, state, patentRoll);
  for (const [name, fn] of STEPS) timed(name, fn, state);
  return state;
}

export function tick(state) {
  if (state.gameOver) return state;
  setInTick(true);
  try { return PROF ? tickProfiled(state) : tickDay(state); } finally { setInTick(false); }
}

function tickDay(state) {
  state.day += 1;
  rollMonth(state, patentRoll);
  tickMarket(state);
  tickLeases(state);
  tickTimers(state);
  tickM3Weather(state); // M3 ops: today's sea state gates today's offshore work
  tickM4Pre(state); // M4: services, markets & weather that gate today's work
  tickDrilling(state);
  tickProduction(state);
  tickRotary(state);
  tickM3Ops(state); // M3 ops (no-op before 1946)
  tickM4Post(state); // M4: shale production, water, gas, oil sands
  tickRefining(state);
  tickSecurity(state);
  tickCrew(state);
  tickSafety(state);
  tickResearch(state);
  tickContracts(state);
  tickM2(state);
  tickM3Econ(state); // M3 econ
  tickM4Econ(state); // M4: finance, politics, fiscal, ESG
  tickFinance(state);
  tickBurnAlert(state);
  tickNews(state);
  tickEra(state);
  tickAchievements(state);
  autosave(state);
  return state;
}

export function runDays(state, n) {
  for (let i = 0; i < n && !state.gameOver; i++) tick(state);
  return state;
}
